import { Button } from './ui/button';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { mockData } from '../utils/mock';

export const Hero = () => {
  const { hero, stats } = mockData;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center pt-16" aria-label="Hero section">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${hero.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
        role="img"
        aria-label="Shipping containers at port - representing international relocation services"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-blue-900/85 to-blue-800/75"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-3xl">
          <div className="inline-block mb-4 px-4 py-2 bg-blue-100/20 backdrop-blur-sm rounded-full">
            <span className="text-blue-100 text-sm font-semibold">{hero.subtitle}</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Professional TOR1 Assistance for People Relocating Back to the UK
          </h1>
          
          <p className="text-2xl text-yellow-300 font-semibold mb-6 leading-relaxed">
            Avoid costly mistakes, customs delays, and rejected applications with expert Transfer of Residence support.
          </p>

          <div className="text-lg text-blue-100 mb-8 leading-relaxed space-y-4">
            <p>
              Returning to the UK after living overseas can feel stressful and confusing. Many people are unsure how the TOR1 process works, worried about customs rules, and concerned about whether their belongings will arrive without problems.
            </p>
            
            <p>
              While anyone can technically submit a TOR1 application themselves, mistakes, missing documents, or poor preparation can lead to delays, complications, or rejection.
            </p>
            
            <p className="font-semibold text-white">
              TOR1 Services provides professional guidance, strategic support, and expert application management to help make your return to the UK smoother and less stressful.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <Button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold text-lg px-8 py-6"
            >
              Book Professional TOR1 Assistance
            </Button>
            <Button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="bg-white hover:bg-gray-100 text-blue-900 font-bold text-lg px-8 py-6"
            >
              Get Advice Before You Apply
            </Button>
          </div>
        </div>

        {/* Content Above Stats */}
        <div className="mt-12 max-w-4xl mx-auto text-lg text-blue-100 leading-relaxed space-y-6">
          <div>
            <p className="font-bold text-white text-xl mb-3">We help customers:</p>
            <ul className="list-none space-y-2 ml-0">
              <li className="flex items-start">
                <span className="text-yellow-300 mr-2">•</span>
                <span>reduce the risk of rejection,</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-300 mr-2">•</span>
                <span>avoid expensive international relocation mistakes,</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-300 mr-2">•</span>
                <span>understand HMRC expectations,</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-300 mr-2">•</span>
                <span>prepare supporting documents correctly,</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-300 mr-2">•</span>
                <span>and return to the UK with greater confidence.</span>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-white text-xl mb-3">Many customers contact us after:</p>
            <ul className="list-none space-y-2 ml-0">
              <li className="flex items-start">
                <span className="text-yellow-300 mr-2">•</span>
                <span>struggling to understand HMRC guidance,</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-300 mr-2">•</span>
                <span>receiving conflicting information online,</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-300 mr-2">•</span>
                <span>submitting incomplete applications,</span>
              </li>
              <li className="flex items-start">
                <span className="text-yellow-300 mr-2">•</span>
                <span>or becoming concerned about customs complications when shipping belongings back to the UK.</span>
              </li>
            </ul>
          </div>

          <p>
            A rejected or poorly prepared TOR1 application can create unnecessary stress, delays, and additional costs during an already emotional relocation.
          </p>

          <p className="font-semibold text-white">
            Our role is to simplify the process and help guide you through it properly.
          </p>
        </div>

        {/* Stats */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl lg:text-4xl font-bold text-white mb-2">{stat.value}</div>
              <div className="text-blue-200 text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
